import { IguestbookItem } from '@typings/guestbook/guestbook.interface';
import { fuego } from '@nandorojo/swr-firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { appendEditHistory } from '@store/firebase/utils/editHistory';

// Firestore 서버 타임스탬프 (클라이언트 시계 오차로 인한 정렬 뒤틀림 방지)
const serverTimestamp = () => firebase.firestore.FieldValue.serverTimestamp();
import {
  NOTI_TYPE,
  NOTI_TYPE_VALUE,
} from '@typings/notification/notificationType';
import { updateUserNotification } from '../notification/update-notification';
import { addAndGetDocId } from '../timeline/update-timeline';
import {
  doc,
  getFirestore,
  runTransaction,
} from 'firebase/firestore';
import { emitTimelineForceRefresh } from '@utils/realtimeFallbackEvent';

type Props = {
  data: IguestbookItem;
  collectionPath: string;
  docId?: string;
  sendToMasters?: boolean;
  notiType?: NOTI_TYPE_VALUE;
  editedBy?: string;
  editedByName?: string; // 비로그인 익명 수정 시 anonymousName
};

export async function updateGuestbook({
  data,
  collectionPath,
  docId,
  notiType,
  sendToMasters = true,
  editedBy,
  editedByName,
}: Props) {
  const ref = fuego.db.collection(collectionPath);

  let _docId: string | undefined = docId || undefined;

  // numberId는 신규 작성 시에만 계산 (수정 시에는 기존 값 유지)
  let numberId: number | undefined;

  if (!_docId) {
    // _meta 컬렉션에서 lastNumberId를 트랜잭션으로 increment하여
    // 동시 업로드 시에도 유일한 numberId를 보장한다.
    try {
      const db = getFirestore();
      const metaRef = doc(db, '_meta', collectionPath);

      numberId = await runTransaction(db, async (transaction) => {
        const metaSnap = await transaction.get(metaRef);
        const last = metaSnap.exists() ? (metaSnap.data()?.lastNumberId ?? 0) : 0;
        const next = (last as number) + 1;
        transaction.set(metaRef, { lastNumberId: next }, { merge: true });
        return next;
      });
    } catch {
      // 비인증 사용자의 경우 Firestore 보안 규칙으로 트랜잭션이 실패할 수 있음
      // numberId 없이 게시글 생성을 계속 진행
    }
  }

  // 수정 이력: Firestore에서 누적된 editHistory를 읽고, 새 내용(스냅샷)을 히스토리에 저장
  let editHistoryPatch: Record<string, any> = {};
  if (docId && (editedBy !== undefined || editedByName)) {
    const currentDoc = await ref.doc(docId).get();
    const currentData = currentDoc.data() as IguestbookItem | undefined;
    editHistoryPatch = {
      lastEditedBy: editedBy || '',
      editHistory: appendEditHistory(
        currentData?.editHistory,
        editedBy || '',
        editedByName,
        data.contents, // 새로 저장하는 내용(스냅샷)
      ),
    };
  }

  const newData: IguestbookItem = {
    ...data,
    editedAt: serverTimestamp() as any,
    ...(numberId !== undefined && { numberId }),
    ...editHistoryPatch,
  };

  if (_docId) {
    // 수정
    await ref
      .doc(_docId)
      .set(newData, { merge: true })
      .catch((err) => console.log(err.message));

    emitTimelineForceRefresh({
      collectionPath,
      action: 'update',
      docId: _docId,
      patch: newData as Record<string, any>,
    });
  }

  if (!_docId) {
    // 추가
    _docId = await addAndGetDocId({ ref, data: newData });

    // docID (parents Doc) 가 없으면, 본인을 추가해줌
    if (_docId) {
      await ref
        .doc(_docId)
        .set({ boardId: _docId }, { merge: true })
        .catch((err) => console.log(err.message));
    }

    // 알림 추가   -----------------------------
    if (_docId) {
      updateUserNotification({
        data: { ...data, boardId: _docId },
        collectionPath,
        notiType: notiType || NOTI_TYPE.GUESTBOOK,
        sendToMasters,
        guestbook: true,
      });
    }
    // --------------------------------------

    if (_docId) {
      emitTimelineForceRefresh({
        collectionPath,
        action: 'create',
        docId: _docId,
        patch: { ...newData, id: _docId, boardId: _docId } as Record<string, any>,
        reconcileAfterMs: 2000,
      });
    }
  }

  return _docId;
}
